export const LINE_CONNECT_PENDING_SESSION_KEY = 'sceneaccess.lineLinkSession';

const LINE_CONNECT_QUERY_KEYS = ['lineConnect', 'lineSessionId', 'line'];
const SAFE_URL_BASE = 'https://sceneaccess.local';

export const sanitizeLineReturnTo = value => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw || raw[0] !== '/' || raw.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(raw)) {
    return '/';
  }

  try {
    const parsed = new URL(raw, SAFE_URL_BASE);
    const nextReturnTo = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    return nextReturnTo && !nextReturnTo.startsWith('//') ? nextReturnTo : '/';
  } catch (e) {
    return '/';
  }
};

export const getCurrentLineReturnTo = () => {
  if (typeof window === 'undefined') {
    return '/';
  }

  return sanitizeLineReturnTo(
    `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`
  );
};

export const appendLineConnectParams = (returnTo, { status, sessionId } = {}) => {
  const safeReturnTo = sanitizeLineReturnTo(returnTo);
  const parsed = new URL(safeReturnTo, SAFE_URL_BASE);

  if (status) {
    parsed.searchParams.set('lineConnect', status);
  }
  if (sessionId) {
    parsed.searchParams.set('lineSessionId', sessionId);
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

export const getLineConnectResultFromSearch = search => {
  const params = new URLSearchParams(search || '');
  const status = params.get('lineConnect');
  const lovableLineStatus = params.get('line');

  if (status) {
    return {
      status: status === 'connected' ? 'success' : status,
      sessionId: params.get('lineSessionId') || null,
    };
  }

  if (lovableLineStatus) {
    return {
      status: lovableLineStatus === 'connected' ? 'success' : lovableLineStatus,
      sessionId: null,
      line: lovableLineStatus,
    };
  }

  return null;
};

export const stripLineConnectParamsFromSearch = search => {
  const params = new URLSearchParams(search || '');
  LINE_CONNECT_QUERY_KEYS.forEach(key => params.delete(key));
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage || null;
  } catch (e) {
    return null;
  }
};

export const isLineLinkSessionExpired = session => {
  const expiresAt = Date.parse(session?.expiresAt || '');
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
};

export const readPendingLineLinkSession = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(LINE_CONNECT_PENDING_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const writePendingLineLinkSession = session => {
  const storage = getSessionStorage();
  const sessionId = session?.sessionId;
  if (!storage || !sessionId) {
    return;
  }

  const pendingSession = {
    ...session,
    returnTo: sanitizeLineReturnTo(session.returnTo),
    createdAt: session.createdAt || new Date().toISOString(),
  };

  try {
    storage.setItem(LINE_CONNECT_PENDING_SESSION_KEY, JSON.stringify(pendingSession));
  } catch (e) {
    // Storage failures should not block the LINE connect flow.
  }
};

export const clearPendingLineLinkSession = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(LINE_CONNECT_PENDING_SESSION_KEY);
  } catch (e) {
    // Ignore storage cleanup failures.
  }
};
